import React from 'react';
import styled from 'styled-components';
import {CSSTransition} from 'react-transition-group';
import {FormattedMessage, injectIntl} from "react-intl";
import Confirm from "./Confirm";
import Toast from "./Toast";
import H2 from "./H2";
import {FormControlLabel, Radio} from "@material-ui/core";
import {
    GENERAL_TIME_OPTIONS,
    WAITING_LIST_RANGE_MODE,
    DEFAULT_MAX_MINUTES_TO_SEARCH_WAITING_LIST_DATE
} from "../Common/constants";
import {
    AdaptedRadioGroup,
    PlainColumnFlexContainer,
    VerticalSpace,
    PlainContainer, ModalPrimary
} from "../Containers/Identify/CommonUI";
import StaticDatePicker from "./StaticDatePicker";
import Moment from "moment";
import Select from "./Select";
import {toShortDate} from "@tomeravni/easybizy-js-common/date";
import Expandable from "./ExpandableComponent";
import DaysOfWeekSelector from "./DaysOfWeekSelector";
import {
    cleanArr,
    getSafely,
    isNonEmptyObject, isNonEmptyString
} from "@tomeravni/easybizy-js-common/common";
import MultiSelect from "./MultiSelect";
import P from "./P";
import Sizes from "../Assets/Sizes";
import {LinearProgress} from "rmwc/LinearProgress/index";
import {LoaderWrapper} from "./Layout";
import {convertToRecurrenceRule, parseDayParts} from "../Common/Helpers";
import {extractError} from "@tomeravni/easybizy-js-common/errors";

const Wrapper = styled.div`
    .mask-enter {
        opacity: 0;
    }

    .mask-enter-active {
        opacity: 1;
        transition: all 500ms;
    }

    .mask-exit {
        opacity: 1;
    }

    .mask-exit-active {
        opacity: 0;
        transition: all 500ms;
    }

    .alert-enter {
        transform: translateY(100vh);
    }

    .alert-enter-active {
        transform: translateY(0);
        transition: transform 0.5s ease-in;
    }

    .alert-exit {
        transform: translateY(0);
    }

    .alert-exit-active {
        transform: translateY(100vh);
        transition: transform 0.5s ease-in;
    }
`;

const Mask = styled.div`
    position: fixed;
    background-color: rgba(0, 0, 0, 0.3);
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    z-index: 3;
`;

const Message = styled.div`
    direction: ${p => p.ltr ? 'ltr' : 'rtl'};
    position: fixed;
    width: 100%;
    background-color: ${p => p.theme.colors.modalBackground};
    bottom: 0;
    left: 0;
    z-index: 4;
    color: ${p => p.theme.colors.safeText};

    .items-picker__item {
        min-height: 0;
        margin: 0;
    }
`;


const RegisterButton = styled(ModalPrimary)`
    width: 100%;
    min-height: 50px;
    font-size: ${Sizes.sloganTextSize};
`;

const ModalRadioGroup = styled(FormControlLabel)`
    color: ${p => p.theme.colors.modalPrimaryColor} !important;
`

class WaitingListPopOver extends React.PureComponent {
    state = {
        errorMessage: null
    }

    getWrappedSelectedDate = () => {
        const pickDateOption = GENERAL_TIME_OPTIONS.slice()[1];
        const labelDate = toShortDate(this.props.selectedDate)
        const labelPrefix = this.props.intl.formatMessage({id: 'card.expiryPlaceholder'});
        const currentLabel = `${labelPrefix}: ${labelDate}`;
        return Object.assign({}, pickDateOption, {label: currentLabel, translateName: false})
    }

    toggleTimeOption = (option) => {
        // Cannot uncheck single option
        if (option.checked && this.props.timingOptions.filter(opt => (opt.value !== option.value && opt.checked)).length === 0) {
            return;
        }

        this.props.toggleDayPart(option);
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (prevProps.hidden && !this.props.hidden) {
            this.ensureSelectedDateIsWorkingDay();
        }

        if (isNonEmptyObject(this.props.registeringWaitingListError)) {
            const errorMessage = extractError(this.props.registeringWaitingListError);
            if (errorMessage !== this.state.errorMessage) {
                this.setState({errorMessage});
                this.toast.getWrappedInstance().show(errorMessage);
            }
        } else if (isNonEmptyString(this.state.errorMessage)) {
            this.setState({errorMessage: null});
        }
    }

    pickDate = () => {
        return this.datePicker.show();
    }

    dateSelected = (date) => {
        this.props.onChangeSelectedDate(date);
    }

    isDayDisabled = (date) => {
        const daysOfWork = this.props.daysOfWork;
        if (!Array.isArray(daysOfWork) || !date) {
            return false;
        }

        return !daysOfWork[date.getDay()];
    }

    nextWorkingDay = (date) => {
        const daysOfWork = this.props.daysOfWork;
        if (!Array.isArray(daysOfWork) || !daysOfWork.some(Boolean)) {
            return date;
        }

        const result = Moment(date);
        let guard = 0;
        while (!daysOfWork[result.day()] && guard < 7) {
            result.add(1, 'day');
            guard++;
        }

        return result.toDate();
    }

    ensureSelectedDateIsWorkingDay = () => {
        if (!this.isDayDisabled(this.props.selectedDate)) {
            return;
        }

        this.props.onChangeSelectedDate(this.nextWorkingDay(this.props.selectedDate));
        this.props.onChangeRangeType(WAITING_LIST_RANGE_MODE.WEEK_DAYS);
    }

    register = () => {
        let message;
        const isWeekDaysMode = this.props.rangeType === WAITING_LIST_RANGE_MODE.WEEK_DAYS;
        if (!isWeekDaysMode && !this.props.selectedDate) {
            message = this.props.intl.formatMessage({id: 'must_pick_date'})
        }

        if (isWeekDaysMode && !this.props.selectedDaysOfWeek.some((x) => x)) {
            message = this.props.intl.formatMessage({id: 'you_must_include_at_least_one_day'})
        }

        if (message) {
            this.toast.getWrappedInstance().show(message);
            return;
        }

        const rangeRelatedToExtend = !isWeekDaysMode ? {
            specificDate: this.props.selectedDate
        } : {
            range: {
                start: Moment().startOf('day').toDate(),
                end: this.maxFutureDate,
                recurrence: convertToRecurrenceRule(this.props.selectedDaysOfWeek)
            }
        }

        const {services: rawServices, employeeId, dayPart, customer} = this.props.userProps;
        const services = rawServices.map((x) => ({
            metadataId: x.OnlineBookingServiceId,
            type: "OnlineBookingService",
            label: x.ServiceRepresentativeName,
            lengthInMinutes: x.Duration
        }))

        const waitingListItem = Object.assign(rangeRelatedToExtend, {
            services,
            employeeIds: cleanArr([employeeId]),
            dayPart: {
                exact: false,
                dayParts: parseDayParts(dayPart)
            },
            expirationDate: this.maxFutureDate,
            customer,
            totalLengthInMinutes: services.reduce((acc, service) => acc + service.lengthInMinutes, 0)
        })

        this.props.onRegisterWaitingList(waitingListItem, (err) => {
            if (!err) {
                this.props.onSuccess();
            }
        })

    }

    get maxFutureDate() {
        return Moment().add(getSafely(['config', 'MaximumMeetingTimeFromNow'],
            this.props, DEFAULT_MAX_MINUTES_TO_SEARCH_WAITING_LIST_DATE), 'minutes').toDate();
    }


    render() {
        const hidden = this.props.hidden;
        const ltr = !this.props.intl.locale.includes('he');

        let wrappedSelectedDate = [this.getWrappedSelectedDate()];

        return (
            <Wrapper>
                <CSSTransition timeout={300} in={!hidden} unmountOnExit classNames="mask">
                    <Mask onClick={() => this.props.hide()}/>
                </CSSTransition>
                <CSSTransition timeout={500} in={!hidden} unmountOnExit classNames="alert">
                    <Message>
                        <H2 invertedColors={true} semiBold={true} center={true}>
                            <FormattedMessage id="waiting_list"/>
                        </H2>
                        <VerticalSpace/>
                        <PlainColumnFlexContainer>
                            <AdaptedRadioGroup
                                value={this.props.rangeType}
                                row
                                aria-labelledby="demo-row-radio-buttons-group-label"
                                name="row-radio-buttons-group"
                            >
                                <ModalRadioGroup value={WAITING_LIST_RANGE_MODE.EXACT_DATE} control={<Radio/>}
                                                 label={<FormattedMessage id="exact_date"/>}
                                                 onClick={() => this.props.onChangeRangeType(WAITING_LIST_RANGE_MODE.EXACT_DATE)}/>
                                <ModalRadioGroup value={WAITING_LIST_RANGE_MODE.WEEK_DAYS} control={<Radio/>}
                                                 label={<FormattedMessage id="day_of_week"/>}
                                                 onClick={() => this.props.onChangeRangeType(WAITING_LIST_RANGE_MODE.WEEK_DAYS)}/>

                            </AdaptedRadioGroup>
                            <Expandable isExpanded={this.props.rangeType === WAITING_LIST_RANGE_MODE.EXACT_DATE}>
                                <Select
                                    ltr={ltr}
                                    selected={new Date()}
                                    onClick={() => this.pickDate()}
                                    items={wrappedSelectedDate} keyProp="value"
                                    preventPaddingBottom={true}
                                    nameProp="label"/>
                            </Expandable>
                            <Expandable isExpanded={this.props.rangeType === WAITING_LIST_RANGE_MODE.WEEK_DAYS}>
                                <PlainContainer>
                                    <DaysOfWeekSelector
                                        onChange={(days) => this.props.onChangeSelectedDaysOfWeek(days)}
                                        selectedDays={this.props.selectedDaysOfWeek}
                                        days={this.props.daysOfWork}
                                    ></DaysOfWeekSelector>
                                </PlainContainer>
                            </Expandable>
                            <VerticalSpace/>

                            <PlainContainer>
                                <P invertedColors={true} preventPadding={true}><FormattedMessage
                                    id="schedule.timeAtDay"/></P>
                                <MultiSelect options={this.props.timingOptions}
                                             toggleOption={(option) => this.toggleTimeOption(option)}
                                             ltr={ltr}
                                />

                            </PlainContainer>
                        </PlainColumnFlexContainer>
                        <VerticalSpace/>
                        <PlainContainer>
                            <VerticalSpace/>
                            <RegisterButton onClick={() => this.register()}><FormattedMessage
                                id="register"/></RegisterButton>
                        </PlainContainer>
                        <VerticalSpace/>
                        <CSSTransition timeout={300} in={this.props.registeringWaitingList} unmountOnExit
                                       classNames="mask">
                            <div>
                                <Mask/>
                                <LoaderWrapper loading={this.props.registeringWaitingList}>
                                    <LinearProgress determinate={false}/>
                                </LoaderWrapper>
                            </div>
                        </CSSTransition>
                    </Message>
                </CSSTransition>
                <Confirm ref={dialog => this.dialog = dialog} ltr={ltr}/>
                <Toast ref={(toast) => this.toast = toast}/>
                <StaticDatePicker
                    innerRef={(r) => this.datePicker = r}
                    minDate={new Date()} value={new Date()}
                    dateSelected={(date) => this.dateSelected(date)}
                    shouldDisableDate={(date) => this.isDayDisabled(date)}
                    maxDate={this.maxFutureDate}/>

            </Wrapper>
        );
    }
}

export default injectIntl(WaitingListPopOver, {withRef: true});
